import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import {
  DataTable,
  DateRange,
  FeedbackStoreRankingModel,
  FeedbackTypeRankingModel,
  MoneyFlowInfoModel,
  MoneyFlowModel,
  PagedList,
  ProductReleaseReportModel,
  ReportProductModel,
  StorageFlowModel,
  SupplierPaymentModel,
  UserReceivedOrderModel
} from '../interface';
import { toHttpParams } from '../utils/globals';
import {
  ActionReportModel, CancelOrderModel, CashFlowStatementModel,
  FeedbackResourceService,
  InventoryManagementModel, InvoiceModel, ManagerSalesModel, OrderAction,
  OrganizationBalanceSnapshotModel,
  PagedDeliveryOrderResponse,
  PagedRfmClientAnalysisModel,
  PagedStatisticsClientSalesModel,
  PagedStatisticsEmployeeSalesModel, PaymentScheduleModel,
  PaymentType,
  ProductInfoShortModel, ProductStatus, ProfitLossStatementModel, ReceivablesPayablesModel,
  ReportResourceService,
  RevenueByProductModel,
  RevenueModel,
  RfmAnalysisModel,
  RfmUtmAnalysisModel, SalesByCategoryModel, SalesByProductModel,
  SaveOrganizationBalanceSnapshotRequest,
  SortType,
  SourceSystem,
  StatisticsAbcProductsModel,
  StatisticsCategoryPopularProductsModel,
  StatisticsCategoryProductSalesModel,
  StatisticsCategoryProductsTimeModel,
  StatisticsCitySalesModel,
  StatisticsOrdersDayOfWeekModel,
  StatisticsOrdersTimeModel,
  StatisticsPaymentTypesModel,
  StatisticsPopularProductsModel,
  StatisticsProductSalesModel,
  StatisticsProductsModel,
  StatisticsProductSourceTypesModel,
  StatisticsSalesModel,
  StatisticsSourceTypesModel,
  StatisticsStoreSalesModel,
  StoreBasicModel,
  StoreProductInfoModel,
  TotalSpendingModel,
  UnitOfMeasureModel,
  UserBasicModel
} from '../vn-api';
import { TimeInterval } from '../enum';

@Injectable({
  providedIn: 'root'
})
export class HxReportService {

  constructor(
    private http: HttpClient,
    private reportResourceService: ReportResourceService,
    private legacy: ReportResourceService,
    private feedbackResourceService: FeedbackResourceService,
  ) {
  }

  getIncomeOutcome(params?: {
    storeId?: number,
    date?: string,
    categoryIds?: number[],
  }): Promise<IncomeOutcomeModel> {
    return firstValueFrom(this.legacy.incomeOutcome(params?.categoryIds, params?.date, params?.storeId)) as unknown as Promise<IncomeOutcomeModel>;
  }

  getDebts(params: { date?: DateRange, debtor?: boolean, storeIds?: number[] }): Observable<any> {
    return this.legacy.debts(params.date?.from, params.date?.to, params.debtor, params.storeIds);
  }

  getRealizedProductGenerate(params: {
    storeId?: number,
    page?: number,
    limit?: number,
    date?: DateRange,
  }): Observable<any> {
    return this.http.get(`/api/vn/reports/realized-products/generate`, {params: toHttpParams(params, true), responseType: 'arraybuffer', observe: 'response'});
  }

  getRealizedProduct(params: {
    storeId?: number,
    page?: number,
    limit?: number,
    date?: DateRange,
  }): Observable<any> {
    return this.legacy.realizedProducts(params.date?.from, params.date?.to, params.limit, params.page, params.storeId);
  }

  getStorageReport(params?: {
    storeId?: number,
    date?: DateRange,
    productInfoIds?: number[],
    categoryIds?: number[],
  }): Observable<any> {
    return this.legacy.storageStatistics(params?.categoryIds, params?.date?.from, params?.date?.to, params?.productInfoIds, params?.storeId);
  }

  productRelease(params: ReportParams): Observable<PagedList<ProductReleaseReportModel>> {
    return this.legacy.productRelease(params.date?.from, params.date?.to, params.limit, params.page, params.productInfoIds, params.storeId) as unknown as Observable<PagedList<ProductReleaseReportModel>>;
  }

  generateProductRelease(params: ReportParams): Observable<HttpResponse<ArrayBuffer>> {
    return this.http.get(`/api/vn/reports/product-release/generate`, {params: toHttpParams(params, true), responseType: 'arraybuffer', observe: 'response'});
  }

  productShipmentReceived(params: ReportParams): Observable<DataTable<ReportProductModel>> {
    return this.legacy.productShipmentReceived(params.categoryIds, params.date?.from, params.date?.to, params.limit, params.page, params.paymentTypes, params.storeIds ?? []) as unknown as Observable<DataTable<ReportProductModel>>;
  }

  generateProductShipmentReceived(params: ReportParams): Observable<any> {
    return this.http.get(`/api/vn/reports/product-shipment-received/generate`, {params: toHttpParams(params, true), responseType: 'arraybuffer', observe: 'response'});
  }

  productShipmentRefunded(params: ReportParams): Observable<DataTable<ReportProductModel>> {
    return this.legacy.productShipmentRefunded(params.categoryIds, params.date?.from, params.date?.to, params.limit, params.page, params.paymentTypes, params.storeIds ?? []) as unknown as Observable<DataTable<ReportProductModel>>;
  }

  generateProductShipmentRefunded(params: ReportParams): Observable<any> {
    return this.http.get(`/api/vn/reports/product-shipment-refunded/generate`, {params: toHttpParams(params, true), responseType: 'arraybuffer', observe: 'response'});
  }

  generateProductShipment(params: ReportParams): Observable<any> {
    return this.http.get(`/api/vn/reports/product-shipment/generate`, {params: toHttpParams(params, true), responseType: 'arraybuffer', observe: 'response'});
  }

  productSale(params: ReportParams): Observable<DataTable<ReportProductModel>> {
    return this.legacy.productSale(params.categoryIds, params.date?.from, params.date?.to, params.limit, params.page, params.paymentTypes, params.storeIds ?? []) as unknown as Observable<DataTable<ReportProductModel>>;
  }

  generateProductSale(params: ReportParams): Observable<any> {
    return this.http.get(`/api/vn/reports/product-sale/generate`, {params: toHttpParams(params, true), responseType: 'arraybuffer', observe: 'response'});
  }

  productRefund(params: ReportParams): Observable<DataTable<ReportProductModel>> {
    return this.legacy.productRefund(params.categoryIds, params.date?.from, params.date?.to, params.limit, params.page, params.paymentTypes, params.storeIds ?? []) as unknown as Observable<DataTable<ReportProductModel>>;
  }

  generateProductRefund(params: ReportParams): Observable<any> {
    return this.http.get(`/api/vn/reports/product-refund/generate`, {params: toHttpParams(params, true), responseType: 'arraybuffer', observe: 'response'});
  }

  productRevenue(params: ReportParams): Observable<DataTable<ReportProductModel>> {
    return this.legacy.productRevenue(params.categoryIds, params.date?.from, params.date?.to, params.limit, params.page, params.paymentTypes, params.storeIds ?? []) as unknown as Observable<DataTable<ReportProductModel>>;
  }

  generateProductRevenue(params: ReportParams): Observable<any> {
    return this.http.get(`/api/vn/reports/product-revenue/generate`, {params: toHttpParams(params, true), responseType: 'arraybuffer', observe: 'response'});
  }

  closeDebt(body: DebtParams): Observable<any> {
    return this.http.post(`/api/vn/orders/close-debt`, body);
  }

  getFeedbackStoreRating(params: { date?: DateRange, storeIds?: number[] }): Observable<FeedbackStoreRankingModel[]> {
    return this.feedbackResourceService.getStoreRatings(params.date?.from, params.date?.to, params.storeIds) as unknown as Observable<FeedbackStoreRankingModel[]>;
  }

  getFeedbackTypeRating(params: { date?: DateRange, storeIds?: number[] }): Observable<FeedbackTypeRankingModel[]> {
    return this.feedbackResourceService.getFeedbackTypeRatingsByStoreId(params.date?.from, params.date?.to, params.storeIds) as unknown as Observable<FeedbackTypeRankingModel[]>;
  }

  getUserReceivedOrder(params: ReportParams): Observable<UserReceivedOrderModel[]> {
    return this.legacy.usersOrderReceived(params.date?.from, params.date?.to, params.storeId) as unknown as Observable<UserReceivedOrderModel[]>;
  }

  getMoneyFlowInfos(params: MoneyFlowParams): Observable<MoneyFlowInfoModel[]> {
    return this.legacy.moneyFlowFull(params.date?.from, params.date?.to, params.storeIds) as unknown as Observable<MoneyFlowInfoModel[]>;
  }

  getMoneyFlows(params: MoneyFlowParams): Observable<MoneyFlowModel[]> {
    return this.legacy.moneyFlowByStore(params.date?.from, params.date?.to, params.storeIds) as unknown as Observable<MoneyFlowModel[]>;
  }

  storageFlowReport(params: ReportParams): Observable<PagedList<StorageFlowModel>> {
    return this.legacy.storageFlow(params.categoryIds, params.date?.from, params.date?.to, params.limit, params.page, params.productInfoIds, params.storeIds ?? []) as unknown as Observable<PagedList<StorageFlowModel>>;
  }

  supplierPaymentReport(params: {
    page?: number,
    limit?: number,
    date?: DateRange,
    storeIds?: number[],
    supplierId?: number,
    paid?: boolean,
    filterByCreateDate?: boolean,
    timeInterval?: TimeInterval,
  }): Observable<PagedList<SupplierPaymentModel>> {
    return this.legacy.supplierPayments(params.date?.from, params.date?.to, params.filterByCreateDate, params.limit, params.page, params.paid, params.storeIds ?? [], params.supplierId, params.timeInterval as any) as unknown as Observable<PagedList<SupplierPaymentModel>>;
  }

  //Refact services
  getOrderReport(params?: GetOrdersParams): Promise<PagedDeliveryOrderResponse> {
    return firstValueFrom(this.reportResourceService.getReportOrders(params?.debt, params?.discount, params?.employee, params?.date?.from, params?.limit, params?.orderBy,
      params?.page, params?.paymentType, params?.sale, params?.sort, params?.storeIds, params?.date?.to));
  }

  getRevenue(params: {date?: DateRange, storeIds?: number[], saleUserId?: number}): Promise<RevenueModel[]> {
    return firstValueFrom(this.reportResourceService.getRevenueReport(params.date?.from, params.saleUserId, params.storeIds, params.date?.to));
  }

  getAction(params?: {date?: string, storeId?: number}): Promise<ActionReportModel[]> {
    return firstValueFrom(this.reportResourceService.getActionReport(params?.date, params?.storeId));
  }

  getStoreProductInfo(params?: { storeId?: number }): Promise<StoreProductInfoModel[]> {
    return firstValueFrom(this.reportResourceService.getStoreProductInfoReport(params?.storeId));
  }

  getTotalSpendingsByCategoryAndStores(params?: { fromDate?: string, storeIds?: number[], toDate?: string }): Promise<TotalSpendingModel[]> {
    return firstValueFrom(this.reportResourceService.getTotalSpendingsByCategoryAndStores(params?.fromDate, params?.storeIds, params?.toDate));
  }

  getTotalSpendingsByCategory(params?: { fromDate?: string, storeIds?: number[], toDate?: string }): Promise<TotalSpendingModel[]> {
    return firstValueFrom(this.reportResourceService.getTotalSpendingsByCategory(params?.fromDate, params?.storeIds, params?.toDate));
  }

  getStatisticsSales(params: SalesParams): Promise<StatisticsSalesModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsSales(params.cityId, params.clientId, params.fromDate, params.limit, params.page, params.paymentUserId, params.query, params?.storeIds, params.toDate));
  }

  getStatisticsPaymentTypes(params: SalesParams): Promise<StatisticsPaymentTypesModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsPaymentTypes(params.cityId, params.clientId, params.fromDate, params.limit, params.page, params.paymentUserId, params.query, params?.storeIds, params.toDate));
  }

  getStatisticsSourceTypes(params: SalesParams): Promise<StatisticsSourceTypesModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsSourceTypes(params.cityId, params.clientId, params.fromDate, params.limit, params.page, params.paymentUserId, params.query, params?.storeIds, params.toDate));
  }

  getStatisticsOrdersTime(params: SalesParams): Promise<StatisticsOrdersTimeModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsOrdersTime(params.cityId, params.clientId, params.fromDate, params.limit, params.page, params.paymentUserId, params.query, params?.storeIds, params.toDate));
  }

  getStatisticsOrdersDayOfWeek(params: SalesParams): Promise<StatisticsOrdersDayOfWeekModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsOrdersDayOfWeek(params.cityId, params.clientId, params.fromDate, params.limit, params.page, params.paymentUserId, params.query, params?.storeIds, params.toDate));
  }

  getStatisticsPopularProducts(params: SalesParams): Promise<StatisticsPopularProductsModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsPopularProducts(params.cityId, params.clientId, params.fromDate, params.limit, params.page, params.paymentUserId, params.query, params?.storeIds, params.toDate));
  }

  getStatisticsCitySales(params: SalesParams): Promise<StatisticsCitySalesModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsCitySales(params.cityId, params.clientId, params.fromDate, params.limit, params.page, params.paymentUserId, params.query, params?.storeIds, params.toDate));
  }

  getStatisticsStoreSales(params: SalesParams): Promise<StatisticsStoreSalesModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsStoreSales(params.cityId, params.clientId, params.fromDate, params.limit, params.page, params.paymentUserId, params.query, params?.storeIds, params.toDate));
  }

  getStatisticsClientSales(params: SalesParams): Promise<PagedStatisticsClientSalesModel> {
    return firstValueFrom(this.reportResourceService.getStatisticsClientSales(params.cityId, params.clientId, params.fromDate, params.limit, params.page, params.paymentUserId, params.query, params?.storeIds, params.toDate));
  }

  getStatisticsEmployeeSales(params: SalesParams): Promise<PagedStatisticsEmployeeSalesModel> {
    return firstValueFrom(this.reportResourceService.getStatisticsEmployeeSales(params.cityId, params.clientId, params.fromDate, params.limit, params.page, params.paymentUserId, params.query, params?.storeIds, params.toDate));
  }

  getStatisticsCategoryProductSales(params: { fromDate?: string, storeIds?: number[], toDate?: string }): Promise<StatisticsCategoryProductSalesModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsCategoryProductSales(params.fromDate, params?.storeIds, params.toDate));
  }

  getStatisticsProductSales(params: { fromDate?: string, storeIds?: number[], toDate?: string, categoryId?: number, productInfoId?: number }): Promise<StatisticsProductSalesModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsProductSales(params.categoryId, params.fromDate, params.productInfoId, params?.storeIds, params.toDate));
  }

  getStatisticsProductSourceTypes(params: { fromDate?: string, storeIds?: number[], toDate?: string, categoryId?: number, productInfoId?: number }): Promise<StatisticsProductSourceTypesModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsProductSourceTypes(params.categoryId, params.fromDate, params.productInfoId, params?.storeIds, params.toDate));
  }

  getStatisticsCategoryPopularProducts(params: { fromDate?: string, storeIds?: number[], toDate?: string, categoryId?: number, productInfoId?: number }): Promise<StatisticsCategoryPopularProductsModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsCategoryPopularProducts(params.categoryId, params.fromDate, params.productInfoId, params?.storeIds, params.toDate));
  }

  getStatisticsCategoryProductsTime(params: { fromDate?: string, storeIds?: number[], toDate?: string, categoryId?: number, productInfoId?: number }): Promise<StatisticsCategoryProductsTimeModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsCategoryProductsTime(params.categoryId, params.fromDate, params.productInfoId, params?.storeIds, params.toDate));
  }

  getStatisticsCategoryProductsDayOfWeek(params: { fromDate?: string, storeIds?: number[], toDate?: string, categoryId?: number, productInfoId?: number }): Promise<StatisticsCategoryProductsTimeModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsCategoryProductsDayOfWeek(params.categoryId, params.fromDate, params.productInfoId, params?.storeIds, params.toDate));
  }

  getStatisticsProducts(params: { fromDate?: string, storeIds?: number[], toDate?: string, categoryId?: number, userId?: number }): Promise<StatisticsProductsModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsProducts(params.categoryId, params.fromDate, params?.storeIds, params.toDate, params.userId));
  }

  getStatisticsAbcProductsReport(params: { fromDate?: string, storeIds?: number[], toDate?: string, categoryId?: number, userId?: number }): Promise<StatisticsAbcProductsModel[]> {
    return firstValueFrom(this.reportResourceService.getStatisticsAbcProductsReport(params.categoryId, params.fromDate, params?.storeIds, params.toDate));
  }

  getRfmAnalysis(params: { fromDate?: string, storeIds?: number[], toDate?: string, maxFrequency?: number, maxMonetary?: number, segments?: string[], source?: SourceSystem }): Promise<RfmAnalysisModel[]> {
    return firstValueFrom(this.reportResourceService.getRfmAnalysis(params.fromDate, undefined, params?.maxFrequency, params?.maxMonetary, undefined, params.segments, params.source, params?.storeIds, params.toDate));
  }

  getRfmClientAnalysis(params: { fromDate?: string, storeIds?: number[], toDate?: string, maxFrequency?: number, maxMonetary?: number, segments?: string[], source?: SourceSystem, page?: number, limit?: number }): Promise<PagedRfmClientAnalysisModel> {
    return firstValueFrom(this.reportResourceService.getRfmClientAnalysis(params.fromDate, params?.limit, params?.maxFrequency, params?.maxMonetary, params?.page, params.segments, params.source, params?.storeIds, params.toDate));
  }

  getRfmUtmAnalysis(params: { fromDate?: string, storeIds?: number[], toDate?: string, maxFrequency?: number, maxMonetary?: number, segments?: string[], source?: SourceSystem }): Promise<RfmUtmAnalysisModel[]> {
    return firstValueFrom(this.reportResourceService.getRfmUtmAnalysis(params.fromDate, undefined, params?.maxFrequency, params?.maxMonetary, undefined, params.segments, params.source, params?.storeIds, params.toDate));
  }

  getInventoryManagement(params: { fromDate?: string, toDate?: string, storeId?: number, categoryIds?: number[] }): Promise<InventoryManagementModel[]> {
    return firstValueFrom(this.reportResourceService.getInventoryManagement(params?.categoryIds, params.fromDate, params?.storeId, params.toDate));
  }

  generateInventoryManagement(params: { fromDate?: string, toDate?: string, storeId?: number, categoryIds?: number[] }): Promise<HttpResponse<any>> {
    return firstValueFrom(this.reportResourceService.generateInventoryManagement(params?.categoryIds, params.fromDate, params?.storeId, params.toDate, undefined, 'response', false, { httpHeaderAccept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  }

  getRevenueByProducts(params: { fromDate?: string, toDate?: string, storeIds?: number[], categoryIds?: number[] }): Promise<RevenueByProductModel[]> {
    return firstValueFrom(this.reportResourceService.getRevenueByProducts(params?.categoryIds, params.fromDate, params?.storeIds, params.toDate));
  }

  generateRevenueByProducts(params: { fromDate?: string, toDate?: string, storeIds?: number[], categoryIds?: number[] }): Promise<HttpResponse<any>> {
    return firstValueFrom(this.reportResourceService.generateRevenueByProducts(params?.categoryIds, params.fromDate, params?.storeIds, params.toDate, undefined, 'response', false, { httpHeaderAccept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  }

  getSalesByCategory(params: { fromDate?: string, toDate?: string, storeIds?: number[], categoryIds?: number[], productStatuses?: ProductStatus[] }): Promise<SalesByCategoryModel[]> {
    return firstValueFrom(this.reportResourceService.getSalesByCategory(params?.categoryIds, params.fromDate, params?.productStatuses, params?.storeIds, params.toDate));
  }

  getSalesByProduct(params: { fromDate?: string, toDate?: string, storeIds?: number[], categoryIds?: number[] }): Promise<SalesByProductModel[]> {
    return firstValueFrom(this.reportResourceService.getSalesByProduct(params?.categoryIds, params.fromDate, params?.storeIds, params.toDate));
  }

  generateSalesByCategoryAndProduct(params: { fromDate?: string, toDate?: string, storeIds?: number[], categoryIds?: number[], productStatuses?: ProductStatus[] }): Promise<HttpResponse<any>> {
    return firstValueFrom(this.reportResourceService.generateSalesByCategoryAndProduct(params?.categoryIds, params.fromDate, params?.productStatuses, params?.storeIds, params.toDate, undefined, 'response', false, { httpHeaderAccept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  }

  getProfitLossStatement(params: { fromDate?: string, toDate?: string, storeIds?: number[] }): Promise<ProfitLossStatementModel> {
    return firstValueFrom(this.reportResourceService.getProfitLossStatement(params.fromDate, params?.storeIds, params.toDate));
  }

  generateProfitLossStatement(params: { fromDate?: string, toDate?: string, storeIds?: number[] }): Promise<HttpResponse<any>> {
    return firstValueFrom(this.reportResourceService.generateProfitLossStatement(params.fromDate, params?.storeIds, params.toDate, undefined, 'response', false, { httpHeaderAccept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  }

  getReceivablesPayables(params: { fromDate?: string, toDate?: string, storeId?: number, supplierId?: number }): Promise<ReceivablesPayablesModel[]> {
    return firstValueFrom(this.reportResourceService.getReceivablesPayables(params.fromDate, params?.storeId, params.supplierId, params.toDate));
  }

  getInvoiceInfoReports(params: { fromDate?: string, toDate?: string, storeId?: number, type?: string, productInfoIds?: number[] }): Promise<InvoiceModel[]> {
    return firstValueFrom(this.reportResourceService.getInvoiceInfoReports(params.fromDate, params.productInfoIds, params?.storeId, params.toDate, params.type));
  }

  getCashFlowReports(params: { fromDate?: string, toDate?: string, storeIds?: number[], organizationIds?: number[] }): Promise<CashFlowStatementModel> {
    return firstValueFrom(this.reportResourceService.getCashFlowReports(params.fromDate, params.organizationIds, params.storeIds, params.toDate));
  }

  getOrganizationBalanceSnapshots(organizationIds: number[]): Promise<OrganizationBalanceSnapshotModel[]> {
    return firstValueFrom(this.reportResourceService.getOrganizationBalanceSnapshots(organizationIds));
  }

  saveOrganizationBalanceSnapshot(req: SaveOrganizationBalanceSnapshotRequest): Promise<OrganizationBalanceSnapshotModel> {
    return firstValueFrom(this.reportResourceService.saveOrganizationBalanceSnapshot(req));
  }

  getPaymentSchedule(params: { fromDate?: string, toDate?: string, storeIds?: number[] }): Promise<PaymentScheduleModel[]> {
    return firstValueFrom(this.reportResourceService.getPaymentSchedule(params.fromDate, params.storeIds, params.toDate));
  }

  getCancelOrderReport(params: { fromDate?: string, toDate?: string, storeIds?: number[] }): Promise<CancelOrderModel[]> {
    return firstValueFrom(this.reportResourceService.getCancelOrderReport(params.fromDate, params.storeIds, params.toDate));
  }

  getManagerSalesReport(params: { fromDate?: string, toDate?: string, storeIds?: number[], managerUserIds?: number[], actions?: OrderAction[] }): Promise<ManagerSalesModel[]> {
    return firstValueFrom(this.reportResourceService.getManagerSalesReport(params.actions, params.fromDate, params.managerUserIds, params.storeIds, params.toDate));
  }

  generateManagerSalesReport(params: { fromDate?: string, toDate?: string, storeIds?: number[], managerUserIds?: number[], actions?: OrderAction[] }): Promise<HttpResponse<any>> {
    return firstValueFrom(this.reportResourceService.generateManagerSales(params.actions, params.fromDate, params?.managerUserIds, params?.storeIds, params.toDate, undefined, 'response', false, { httpHeaderAccept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  }
}

export interface GetOrdersParams {
  sale?: number;
  employee?: number;
  date?: DateRange;
  limit?: number;
  page?: number;
  orderBy?: string;
  sort?: SortType;
  paymentType?: PaymentType;
  debt?: boolean;
  discount?: boolean;
  storeIds: number[];
}

interface ReportParams {
  storeId?: number;
  storeIds?: number[];
  page?: number;
  limit?: number;
  productInfoIds?: number[];
  paymentTypes?: PaymentType[];
  categoryIds?: number[];
  date?: DateRange;
}

interface DebtParams {
  dateFrom?: string;
  dateTo?: string;
  storeIds?: number[];
  userIds?: number[];
}

interface MoneyFlowParams {
  date?: DateRange;
  storeIds?: number[];
}

interface SalesParams {
  page?: number,
  limit?: number,
  fromDate: string,
  toDate: string,
  storeIds?: number[],
  query?: string,
  cityId?: number,
  clientId?: number,
  paymentUserId?: number,
}

export interface IncomeOutcomeHeaderModel {
  stores: StoreBasicModel[];
  employees: UserBasicModel[];
}

export interface IncomeOutcomeBodyModel {
  dayBeforeRemainder: number;
  selectedDayRemainder: number;
  productInfo: ProductInfoShortModel;
  unitOfMeasure: UnitOfMeasureModel;
  stores: IncomeOutcomeStoreModel[];
  employees: IncomeOutcomeEmployeeModel[];
}

export interface IncomeOutcomeStoreModel {
  storeId: number;
  dayBeforeRemainder: number;
  income: number;
  selectedDayRemainder: number;
  discardedAmount: number;
  refundedAmount: number;
  exportAmount: number;
  confectionaryAmount: number;
  inventoryAmount: number;
}

export interface IncomeOutcomeEmployeeModel {
  userId: number;
  deliveryAmount: number;
}

export interface IncomeOutcomeModel {
  header: IncomeOutcomeHeaderModel;
  list: IncomeOutcomeBodyModel[];
}

